/*
 * Edible Beauty cart — shared utilities.
 * Dawn-compatible globals (PUB_SUB_EVENTS, subscribe/publish, trapFocus, debounce, fetchConfig,
 * <quantity-input>) plus window.EBCart, the single mutation gateway used by every cart surface.
 * Loaded after bundle.js; defines each global only when nothing else has.
 */
(function () {
  'use strict';

  window.ON_CHANGE_DEBOUNCE_TIMER = window.ON_CHANGE_DEBOUNCE_TIMER || 300;
  window.PUB_SUB_EVENTS = Object.assign(
    { cartUpdate: 'cart-update', quantityUpdate: 'quantity-update', cartError: 'cart-error', variantChange: 'variant-change' },
    window.PUB_SUB_EVENTS || {}
  );

  if (typeof window.subscribe !== 'function' || typeof window.publish !== 'function') {
    var subscribers = {};
    window.subscribe = function (eventName, callback) {
      if (subscribers[eventName] === undefined) subscribers[eventName] = [];
      subscribers[eventName] = subscribers[eventName].concat([callback]);
      return function unsubscribe() {
        subscribers[eventName] = subscribers[eventName].filter(function (cb) { return cb !== callback; });
      };
    };
    window.publish = function (eventName, data) {
      if (!subscribers[eventName]) return Promise.resolve();
      var promises = subscribers[eventName].map(function (callback) {
        try { return callback(data); } catch (e) { console.error('[EB cart] subscriber failed', e); return undefined; }
      });
      return Promise.all(promises);
    };
  }

  if (typeof window.getFocusableElements !== 'function') {
    window.getFocusableElements = function (container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    };
  }

  window.trapFocusHandlers = window.trapFocusHandlers || {};

  if (typeof window.removeTrapFocus !== 'function') {
    window.removeTrapFocus = function (elementToFocus) {
      var h = window.trapFocusHandlers;
      document.removeEventListener('focusin', h.focusin);
      document.removeEventListener('focusout', h.focusout);
      document.removeEventListener('keydown', h.keydown);
      if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
    };
  }

  if (typeof window.trapFocus !== 'function') {
    window.trapFocus = function (container, elementToFocus) {
      if (!container) return;
      elementToFocus = elementToFocus || container;
      var elements = window.getFocusableElements(container);
      var first = elements[0];
      var last = elements[elements.length - 1];
      var h = window.trapFocusHandlers;

      window.removeTrapFocus();

      h.focusin = function (event) {
        if (event.target !== container && event.target !== last && event.target !== first) return;
        document.addEventListener('keydown', h.keydown);
      };
      h.focusout = function () {
        document.removeEventListener('keydown', h.keydown);
      };
      h.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return;
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', h.focusout);
      document.addEventListener('focusin', h.focusin);
      if (typeof elementToFocus.focus === 'function') elementToFocus.focus();
    };
  }

  if (typeof window.debounce !== 'function') {
    window.debounce = function (fn, wait) {
      var t;
      return function () {
        var args = arguments;
        var ctx = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    };
  }

  if (typeof window.fetchConfig !== 'function') {
    window.fetchConfig = function (type) {
      type = type || 'json';
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
      };
    };
  }

  /* <quantity-input> — Dawn-compatible stepper. */
  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        constructor() {
          super();
          this.input = this.querySelector('input');
          this.changeEvent = new Event('change', { bubbles: true });
          if (!this.input) return;
          this.input.addEventListener('change', this.validateQtyRules.bind(this));
          this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
        }

        connectedCallback() {
          this.validateQtyRules();
          this.quantityUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
        }

        disconnectedCallback() {
          if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
        }

        onButtonClick(event) {
          event.preventDefault();
          if (!this.input) return;
          var previousValue = this.input.value;
          var button = event.currentTarget;
          if (button.name === 'plus') {
            if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
              this.input.value = this.input.dataset.min;
            } else {
              this.input.stepUp();
            }
          } else {
            this.input.stepDown();
          }
          /* Tell cart-items this change came from a stepper so it applies it as a delta. Accumulates across
             clicks inside the change debounce, so three quick clicks are +3, not +1. */
          var moved = parseInt(this.input.value, 10) - parseInt(previousValue, 10);
          if (!moved) return;
          this.input.dataset.stepDelta = String(parseInt(this.input.dataset.stepDelta || '0', 10) + moved);
          this.input.dispatchEvent(this.changeEvent);
        }

        validateQtyRules() {
          if (!this.input) return;
          var value = parseInt(this.input.value);
          var minus = this.querySelector(".quantity__button[name='minus']");
          var plus = this.querySelector(".quantity__button[name='plus']");
          if (this.input.min && minus) minus.classList.toggle('disabled', value <= parseInt(this.input.min));
          if (this.input.max && plus) plus.classList.toggle('disabled', value >= parseInt(this.input.max));
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* EBCart — serialised cart mutations + Section Rendering API re-render */
  /* ------------------------------------------------------------------ */
  var queueTail = Promise.resolve();
  var state = { cart: (window.STORE && window.STORE.cart) || null, pending: 0 };

  function routes() { return window.routes || {}; }
  function strings() { return window.cartStrings || {}; }

  function enqueue(task) {
    var run = queueTail.then(task, task);
    queueTail = run.catch(function () {});
    return run;
  }

  function sectionTargets() {
    var targets = [];
    if (document.getElementById('CartDrawer')) {
      targets.push({ section: 'cart-drawer', target: '#CartDrawer', selector: '#CartDrawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ section: 'cart-icon-bubble', target: '#cart-icon-bubble', selector: '.shopify-section' });
    }
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      targets.push({ section: items.dataset.id, target: '#main-cart-items .js-contents', selector: '#main-cart-items .js-contents' });
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      targets.push({ section: footer.dataset.id, target: '#main-cart-footer .js-contents', selector: '#main-cart-footer .js-contents' });
    }
    return targets;
  }

  function sectionNames() {
    var names = [];
    sectionTargets().forEach(function (t) { if (names.indexOf(t.section) === -1) names.push(t.section); });
    return names;
  }

  function renderSections(sections) {
    if (!sections) return;
    sectionTargets().forEach(function (t) {
      var html = sections[t.section];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var source = doc.querySelector(t.selector) || doc.body;
      var target = document.querySelector(t.target);
      if (!target || !source) return;
      target.innerHTML = source.innerHTML;
    });
    syncEmptyState();
    syncCheckoutLock();
    document.dispatchEvent(new CustomEvent('cart:rendered'));
  }

  function syncEmptyState() {
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    var drawer = document.querySelector('cart-drawer');
    if (inner && drawer) drawer.classList.toggle('is-empty', Number(inner.dataset.itemCount || 0) === 0);
    if (state.cart) {
      var empty = state.cart.item_count === 0;
      document.querySelectorAll('cart-items, #main-cart-footer').forEach(function (el) { el.classList.toggle('is-empty', empty); });
    }
  }

  function setBusy(on) {
    state.pending = Math.max(0, state.pending + (on ? 1 : -1));
    var busy = state.pending > 0;
    document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems').forEach(function (el) { el.classList.toggle('cart__items--disabled', busy); });
    document.documentElement.classList.toggle('cart-busy', busy);
    syncCheckoutLock();
  }

  /* No checkout while the cart is changing: mid-update the server cart can briefly hold a charged gift or
     a stale quantity. Buttons we lock are tagged so unlocking restores the server state (disabled when
     the cart is empty). Re-applied after every render, since rendered HTML arrives unlocked. */
  function syncCheckoutLock() {
    var busy = state.pending > 0;
    var hasItems = !state.cart || state.cart.item_count > 0;
    document.querySelectorAll('#CartDrawer-Checkout, #checkout').forEach(function (btn) {
      if (busy) {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.dataset.cartLocked = 'true';
        }
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
        if (btn.dataset.cartLocked) {
          delete btn.dataset.cartLocked;
          btn.disabled = !hasItems;
        }
      }
    });
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(function (el) {
      el.classList.toggle('is-locked', busy);
      if (busy) el.setAttribute('inert', ''); else el.removeAttribute('inert');
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (state.pending > 0 && form && (form.id === 'CartDrawer-Form' || form.id === 'cart')) event.preventDefault();
  }, true);

  function showError(message) {
    document.querySelectorAll('#CartDrawer-CartErrors, #cart-errors').forEach(function (el) {
      el.textContent = message || '';
      el.classList.toggle('is-visible', !!message);
    });
  }

  /* Header marking the theme's own cart requests, so the external-change watcher below ignores them. */
  var OWN_HEADER = 'X-EB-Cart';

  function request(url, options) {
    options = Object.assign({}, options);
    options.headers = Object.assign({}, options.headers, { 'X-EB-Cart': '1' });
    return fetch(url, options).then(function (response) {
      return response.text().then(function (text) {
        var json = null;
        try { json = JSON.parse(text); } catch (e) { json = null; }
        if (!response.ok) {
          var err = new Error((json && (json.description || json.message)) || strings().error || 'Cart error');
          err.status = response.status;
          err.response = json;
          throw err;
        }
        return json;
      });
    });
  }

  function fetchCart() {
    return request((routes().cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } });
  }

  function getCart() {
    return fetchCart().then(function (cart) {
      setCart(cart);
      return cart;
    });
  }

  /* GET the cart sections for the current page. No Accept: application/json here: on /products/* and
     /collections/* Shopify answers that header with the resource's own JSON and ignores `sections`. */
  function fetchSections() {
    var names = sectionNames();
    if (!names.length) return Promise.resolve(null);
    var url = window.location.pathname + '?sections=' + encodeURIComponent(names.join(','));
    return request(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (sections) {
      if (sections && Object.keys(sections).some(function (k) { return names.indexOf(k) !== -1; })) return sections;
      console.warn('[EB cart] section refresh returned no cart sections', sections && Object.keys(sections));
      return null;
    });
  }

  function setCart(cart) {
    state.cart = cart;
    if (window.STORE) window.STORE.cart = cart;
    syncEmptyState();
  }

  function openDrawer(trigger) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open(trigger);
  }

  function closeDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  function announce(source, extra) {
    var payload = Object.assign({ source: source, cartData: state.cart, rendered: true }, extra || {});
    window.publish(window.PUB_SUB_EVENTS.cartUpdate, payload);
  }

  /* Resolvers run on every confirmed cart BEFORE anything is painted, inside the same queued task and
     busy state. The gift engine registers one so an add that unlocks (or loses) a gift is followed by the
     gift add/remove and only then a single repaint. A resolver receives { cart, sections } and returns
     (a promise of) a new { cart, sections }, or null when it changed nothing. Resolvers run inside the
     queue, so they must call the API directly and never EBCart.add/change/refresh (that would deadlock). */
  var resolvers = [];

  function addResolver(fn) {
    if (typeof fn === 'function') resolvers.push(fn);
  }

  function resolve(result) {
    return resolvers.reduce(function (chain, fn) {
      return chain.then(function (current) {
        return Promise.resolve()
          .then(function () { return fn(current); })
          .then(function (next) { return next || current; })
          .catch(function (e) {
            console.error('[EB cart] resolver failed', e);
            return current;
          });
      });
    }, Promise.resolve(result));
  }

  /* The add currently being resolved: lets setStatus open the drawer early (see below). */
  var currentAdd = null;

  /* Status line shown while resolvers work ("Adding your free gift…"). Sits between the progress bar and
     the items in the drawer, and above the items on the cart page. Cleared by the final paint.
     If an add asked to open the drawer and it is still closed, paint the add's own sections into the
     (closed) drawer and open it now, so the shopper sees the message instead of waiting on nothing. */
  function setStatus(message) {
    if (!message) return clearStatus();
    if (currentAdd && currentAdd.open) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && !drawer.classList.contains('active')) {
        setCart(currentAdd.cart);
        renderSections(currentAdd.sections);
        openDrawer(currentAdd.trigger);
      }
      currentAdd.open = false;
    }
    var hosts = [];
    var drawerItems = document.querySelector('#CartDrawer cart-drawer-items');
    if (drawerItems) hosts.push({ parent: drawerItems.parentNode, before: drawerItems });
    var pageItems = document.getElementById('main-cart-items');
    if (pageItems) hosts.push({ parent: pageItems, before: pageItems.firstChild });
    hosts.forEach(function (host) {
      var el = host.parent.querySelector(':scope > .cart-status');
      if (!el) {
        el = document.createElement('div');
        el.className = 'cart-status';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.innerHTML = '<svg class="cart-status__spinner" viewBox="0 0 66 66" aria-hidden="true" focusable="false"><circle fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg><span class="cart-status__text"></span>';
        host.parent.insertBefore(el, host.before);
      }
      el.querySelector('.cart-status__text').textContent = message;
    });
  }

  function clearStatus() {
    document.querySelectorAll('.cart-status').forEach(function (el) { el.remove(); });
  }

  /* One paint per user action: state, then sections, then the event apps listen to. */
  function commit(result, source, extra) {
    clearStatus();
    setCart(result.cart);
    renderSections(result.sections);
    announce(source, extra);
    return result.cart;
  }

  function add(items, opts) {
    opts = opts || {};
    var open = opts.open !== false;
    var source = opts.source || 'api';
    return enqueue(function () {
      setBusy(true);
      showError('');
      var body = JSON.stringify({ items: items, sections: sectionNames(), sections_url: window.location.pathname });
      return request(routes().cart_add_url || '/cart/add', Object.assign(window.fetchConfig(), { body: body }))
        .then(function (result) {
          var added = result.items || [result];
          return fetchCart()
            .then(function (cart) {
              currentAdd = { open: open, trigger: opts.trigger, cart: cart, sections: result.sections };
              return resolve({ cart: cart, sections: result.sections });
            })
            .then(function (final) {
              var cart = commit(final, source, { items: added, variantId: added[0] && added[0].variant_id, wasItemAdded: true });
              document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { items: added, cart: cart, source: source }, bubbles: true }));
              if (open) openDrawer(opts.trigger);
              return cart;
            });
        })
        .catch(function (err) {
          showError(err.message || strings().error);
          window.publish(window.PUB_SUB_EVENTS.cartError, { source: source, errors: err.response || err.message });
          if (open) openDrawer(opts.trigger);
          throw err;
        })
        .finally(function () {
          currentAdd = null;
          clearStatus();
          setBusy(false);
        });
    });
  }

  function mutate(url, payload, opts) {
    opts = opts || {};
    var source = opts.source || 'api';
    return enqueue(function () {
      setBusy(true);
      showError('');
      var body = JSON.stringify(Object.assign({}, payload, { sections: sectionNames(), sections_url: window.location.pathname }));
      return request(url, Object.assign(window.fetchConfig(), { body: body }))
        .then(function (cart) {
          if (cart && cart.errors) {
            var e = new Error(typeof cart.errors === 'string' ? cart.errors : strings().error);
            e.response = cart;
            throw e;
          }
          return resolve({ cart: cart, sections: cart.sections });
        })
        .then(function (final) {
          return commit(final, source, { variantId: opts.variantId, wasItemAdded: false });
        })
        .catch(function (err) {
          showError(err.message || strings().error);
          window.publish(window.PUB_SUB_EVENTS.cartError, { source: source, errors: err.response || err.message });
          throw err;
        })
        .finally(function () {
          clearStatus();
          setBusy(false);
        });
    });
  }

  function change(payload, opts) {
    return mutate(routes().cart_change_url || '/cart/change', payload, Object.assign({ source: 'cart-items' }, opts || {}));
  }

  function update(updates, opts) {
    return mutate(routes().cart_update_url || '/cart/update', { updates: updates }, Object.assign({ source: 'api' }, opts || {}));
  }

  /* Re-read the cart (after an app or another tab changed it). Pass `sections` when the caller already
     has them, e.g. Dawn's renderContents(parsedState) from an app's own /cart/add.js call. */
  function refresh(opts) {
    opts = opts || {};
    return enqueue(function () {
      setBusy(true);
      var sectionsPromise = opts.sections ? Promise.resolve(opts.sections) : fetchSections();
      return Promise.all([sectionsPromise, fetchCart()])
        .then(function (res) { return resolve({ cart: res[1], sections: res[0] }); })
        .then(function (final) { return commit(final, opts.source || 'refresh', { wasItemAdded: !!opts.wasItemAdded }); })
        .finally(function () {
          clearStatus();
          setBusy(false);
        });
    });
  }

  function formDataToItems(formData) {
    var item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) || 1 };
    var sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    var properties = {};
    formData.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== '') properties[m[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return [item];
  }

  window.EBCart = {
    state: state,
    add: add,
    change: change,
    update: update,
    refresh: refresh,
    getCart: getCart,
    fetchCart: fetchCart,
    fetchSections: fetchSections,
    addResolver: addResolver,
    setStatus: setStatus,
    renderSections: renderSections,
    sectionNames: sectionNames,
    sectionTargets: sectionTargets,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    showError: showError,
    formDataToItems: formDataToItems,
    enqueue: enqueue,
    ownHeader: OWN_HEADER,
  };

  /* ------------------------------------------------------------------ */
  /* External cart changes: apps (gift/BOGO, AI assistant, loyalty,     */
  /* store credit…) that write to the Cart API without telling the      */
  /* theme. Watch fetch + XHR writes that aren't ours and re-sync once   */
  /* they finish (no drawer open: that stays the app's call, e.g. via    */
  /* Dawn's cartDrawer.renderContents()).                                 */
  /* ------------------------------------------------------------------ */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var externalTimer = null;

  function isCartWrite(url, method) {
    if (!url) return false;
    var path = String(url);
    try { path = new URL(path, window.location.href).pathname; } catch (e) { /* keep raw */ }
    return CART_WRITE.test(path) && String(method || 'GET').toUpperCase() !== 'GET';
  }

  function hasOwnHeader(headers) {
    if (!headers) return false;
    if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.has(OWN_HEADER);
    return Object.keys(headers).some(function (k) { return k.toLowerCase() === OWN_HEADER.toLowerCase(); });
  }

  function scheduleExternalSync() {
    clearTimeout(externalTimer);
    externalTimer = setTimeout(function () {
      refresh({ source: 'external' }).catch(function () {});
    }, 150);
  }

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = input && typeof input === 'object' && 'url' in input ? input.url : input;
    var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
    var headers = (init && init.headers) || (input && typeof input === 'object' && input.headers);
    var watch = isCartWrite(url, method) && !hasOwnHeader(headers);
    var result = nativeFetch.apply(this, arguments);
    if (watch) result.then(scheduleExternalSync, function () {});
    return result;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSetHeader = XMLHttpRequest.prototype.setRequestHeader;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__ebCartWrite = isCartWrite(url, method);
    this.__ebOwn = false;
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.setRequestHeader = function (name) {
    if (String(name).toLowerCase() === OWN_HEADER.toLowerCase()) this.__ebOwn = true;
    return nativeSetHeader.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__ebCartWrite && !this.__ebOwn) this.addEventListener('loadend', scheduleExternalSync);
    return nativeSend.apply(this, arguments);
  };
})();
